import {useEffect, useRef} from "react";
import {useNavigate} from "react-router";
import {useAuth} from "../AuthProvider/AuthProvider";
import {removeLoginOrigin, setLoginOrigin} from "../../LocalStorageService";
import {sanitisePortalReturnUrl} from "../../global";
import {redeemHandoffTicket} from "../../utils/apiCalls";

/**
 * Completes a sign-in that began on the customer portal (nexus-customer-portal).
 *
 * That portal authenticates against Microsoft Entra and posts the resulting id_token to the API. It cannot hand
 * the session to us directly: this app reads its bearer token out of localStorage, and localStorage is
 * per-origin, so a plain redirect to /Home finds no session and bounces the reader to /Landing to log in a
 * second time.
 *
 * So a ticket travels in the URL fragment instead, and this route spends it: one POST to the API returns the
 * same `{access, refresh, user}` a normal login does, and the reader is logged in through the same
 * AuthProvider.login() the Landing page uses. The ticket is single-use and lives about a minute — it used to be
 * the access and refresh tokens themselves, good for a day and a week and replayable throughout.
 *
 * The fragment is cleared before anything renders. Anything wrong with it, or with the exchange, sends the
 * reader to /Landing. There is deliberately no error screen: a half-finished handoff is indistinguishable from
 * not being logged in, and that is what /Landing is for.
 */
export const EntraHandoff = () => {
    const navigate = useNavigate();
    const {login} = useAuth();

    /*
     * The effect must run its course exactly once. Under React 18's StrictMode every effect is mounted twice
     * in development, and the second run would find a fragment this one had already cleared — and, worse, a
     * ticket this one had already spent, which the API refuses by design.
     */
    const consumed = useRef(false);

    useEffect(() => {
        if (consumed.current) return;
        consumed.current = true;

        /*
         * Cleared before the ticket is used, not after, and with replaceState rather than by assigning to
         * location.hash: assignment would push another history entry, leaving the ticket one Back press away.
         * replaceState overwrites the entry the reader arrived on, so the address bar and this tab's history
         * both stop holding it.
         */
        const handoff = new URLSearchParams(window.location.hash.slice(1));
        window.history.replaceState(null, "", window.location.pathname);

        const ticket = handoff.get("ticket");

        if (!ticket) {
            navigate("/Landing", {replace: true});
            return;
        }

        /*
         * Anything already stored is cleared up front. It describes an earlier session, and if this handoff
         * fails or comes back with no portal, leaving it behind would send this reader — or the next one on this
         * browser — to a portal that is not theirs on sign-out.
         */
        removeLoginOrigin();

        redeemHandoffTicket(ticket)
            .then((session) => {
                /*
                 * All three are required. `user` drives every feature gate, and `access`/`refresh` are what the
                 * axios interceptors in index.js read; a session missing any of them would render a shell that
                 * 401s on its first request, which reads as the portal being broken rather than a failed login.
                 */
                if (!session?.access || !session?.refresh || !session?.user) {
                    console.error("Sign-in handoff returned an incomplete session.");
                    navigate("/Landing", {replace: true});
                    return;
                }

                /*
                 * Where to return this reader on sign-out, as the API reported it: the portal_url of the row
                 * this sign-in arrived from, resolved there from the request's Origin header. It used to arrive
                 * in the fragment above and be checked against a configured allowlist — which meant every new
                 * customer needed an environment variable updated before their sign-out worked.
                 *
                 * null is the ordinary answer for a login that did not come through a portal, and leaves the
                 * reader going to /Landing. Signing out otherwise strands a portal customer on a Tape Ark
                 * password form they have no password for.
                 */
                const returnUrl = sanitisePortalReturnUrl(session.portal_return_url);

                if (session.portal_return_url && !returnUrl) {
                    // The API named somewhere we will not navigate to. Nothing the reader can act on, but it
                    // means a portal_url in the database is not an http(s) address, which is worth finding.
                    console.warn("Sign-in handoff returned a portal address that cannot be navigated to.");
                }

                /*
                 * Recorded only now, so a failed exchange never leaves a return origin behind — and after
                 * login(), not before, because login() clears the origin as part of starting a session. Reversed,
                 * these two lines would store this portal and immediately discard it, and every window of this
                 * browser would sign out onto a Tape Ark password form instead of back here.
                 */
                login(session);

                if (returnUrl) {
                    setLoginOrigin(returnUrl);
                }

                navigate("/Home", {replace: true});
            })
            .catch((error) => {
                // Ordinarily a ticket that has expired or been spent — a reload of this address, most likely,
                // since the fragment is gone but the history entry is not. The reader signs in again from the
                // portal; there is nothing they can do here.
                console.error("Sign-in handoff could not be completed.", error);
                navigate("/Landing", {replace: true});
            });
    }, [login, navigate]);

    return null;
};
